import styled, { css } from 'styled-components';
import { IconPointFilled } from '@u21/tabler-icons';
import { getDOMProps } from 'app/shared/utils/react';
import { HTMLProps } from 'react';
import { ColorSchema } from 'vendor/material-minimal/palette';

export interface U21PulseIndicatorProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'size'
> {
  color?: ColorSchema;
  count?: number;
  size?: 'small' | 'medium' | 'large';
}

const SIZES: Record<
  Exclude<U21PulseIndicatorProps['size'], undefined>,
  number
> = {
  small: 24,
  medium: 32,
  large: 48,
};

export const U21_PULSE_INNER_TIMING_FUNCTION =
  'cubic-bezier(0.455, 0.03, 0.515, 0.955)';

export const U21_PULSE_INNER_ANIMATION = `1.25s ${U21_PULSE_INNER_TIMING_FUNCTION} -0.4s infinite`;

export const U21PulseIndicator = (props: U21PulseIndicatorProps) => {
  const { color = 'primary', count, size = 'medium', ...rest } = props;
  return (
    <Container {...getDOMProps(rest)}>
      <Inner $color={color} $count={count} size={SIZES[size]} />
      <Outer $color={color} $count={count} size={SIZES[size] * 3} />
    </Container>
  );
};

// styles referenced from https://github.com/farizhermawan/react-pulse-dot;
const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface StyleProps {
  $color: ColorSchema;
  $count?: number;
}

const COMMON_STYLES = css<StyleProps>`
  color: ${(props) => props.theme.palette[props.$color].main};

  ${(props) =>
    typeof props.$count === 'number'
      ? css`
          animation-iteration-count: ${props.$count};
        `
      : css``}
`;

const Inner = styled(IconPointFilled)<StyleProps>`
  position: absolute;
  animation: pulseDotInner ${U21_PULSE_INNER_ANIMATION};

  @keyframes pulseDotInner {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }

  ${COMMON_STYLES}
`;

const Outer = styled(IconPointFilled)<StyleProps>`
  position: absolute;
  animation: pulseDotOuter 1.25s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
  animation-fill-mode: forwards;

  @keyframes pulseDotOuter {
    0% {
      transform: scale(0.5);
    }
    80%,
    100% {
      opacity: 0;
    }
  }

  ${COMMON_STYLES}
`;
